<script setup>
import { ref } from "vue";
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router";

import UnsavedChangesModal from "./UnsavedChangesModal.vue";

const props = defineProps({
  // Whether there are unsaved changes to guard against.
  dirty: { type: Boolean, required: true },
  // Persists the changes WITHOUT navigating. Its promise drives the modal's save button; if
  // it rejects, the error surfaces there and the modal stays open with navigation blocked.
  save: { type: Function, required: true },
  // Also guard in-place route updates (same component, changed params/query) — e.g. a page
  // that swaps sections via a query param while staying mounted.
  guardUpdate: { type: Boolean, default: false },
});

// Emitted when the user chooses to leave (save or discard). Pages whose component stays
// mounted across the transition (guardUpdate) use it to reset their dirty flag.
const emit = defineEmits(["proceed"]);

const isOpen = ref(false);
let resolveDecision = null; // set only while the modal is open

// Registered from this child component; it attaches to the nearest matched route record, so
// it fires for the parent page even when the page is lazily loaded (where the Options
// `beforeRouteLeave` hook would not — see utils/load.js lazyRoute).
function guard() {
  if (!props.dirty) return true;
  // A prior prompt is still pending (the guard re-fired): abandon it, keep the user put.
  resolveDecision?.(false);
  isOpen.value = true;
  return new Promise((resolve) => {
    resolveDecision = resolve;
  });
}

onBeforeRouteLeave(guard);
if (props.guardUpdate) onBeforeRouteUpdate(guard);

function settle(proceed) {
  isOpen.value = false;
  resolveDecision?.(proceed);
  resolveDecision = null;
  if (proceed) emit("proceed");
}

function cancel() {
  settle(false);
}

function discard() {
  settle(true);
}

async function onSave() {
  try {
    await props.save();
  } catch (error) {
    // Save failed: close the modal and cancel navigation so the page's error toast is
    // visible (the modal would otherwise cover it) and the user stays to retry. The edits
    // are still dirty, so the guard will prompt again on the next navigation attempt.
    settle(false);
    throw error;
  }
  // Saved — close the modal and let the original navigation proceed.
  settle(true);
}
</script>

<template>
  <UnsavedChangesModal :is-open :save-action="onSave" @discard="discard" @cancel="cancel" />
</template>
